import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Calendar, 
  ArrowLeft, 
  RotateCcw, 
  Clock, 
  User, 
  Gift, 
  CheckCircle2, 
  Sparkles 
} from 'lucide-react';

export const AgeCalculator = () => {
  const { withAd, toast } = useAdRedirect();
  const [birthDate, setBirthDate] = useState('2000-01-01');
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);

  // Client-side age calculation logic
  const calculateAge = () => {
    if (!birthDate || !targetDate) return null;
    const bDate = new Date(birthDate);
    const tDate = new Date(targetDate);

    if (bDate > tDate) return { error: 'Birth date cannot be in the future!' };

    let years = tDate.getFullYear() - bDate.getFullYear();
    let months = tDate.getMonth() - bDate.getMonth();
    let days = tDate.getDate() - bDate.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(tDate.getFullYear(), tDate.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    // Total metrics
    const timeDiff = tDate.getTime() - bDate.getTime();
    const totalDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const totalHours = totalDays * 24;
    const totalMinutes = totalHours * 60;

    // Day of the week born
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const bornDayOfWeek = daysOfWeek[bDate.getDay()];

    // Next birthday countdown
    let nextBday = new Date(tDate.getFullYear(), bDate.getMonth(), bDate.getDate());
    if (nextBday < tDate) {
      nextBday = new Date(tDate.getFullYear() + 1, bDate.getMonth(), bDate.getDate());
    }
    const bdayDiff = nextBday.getTime() - tDate.getTime();
    const daysToBday = Math.ceil(bdayDiff / (1000 * 60 * 60 * 24));

    return { years, months, days, totalDays, totalHours, totalMinutes, bornDayOfWeek, daysToBday };
  };

  const age = calculateAge();

  const handleReset = () => {
    setBirthDate('2000-01-01');
    setTargetDate(new Date().toISOString().split('T')[0]);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Age Calculator Online - Exact Age & Days" 
        description="Calculate your exact age in years, months, days, hours, and minutes. Check upcoming birthday countdowns and day of birth instantly."
        keywords="age calculator, calculate exact age, chronological age calculator, days lived calculator, birthday countdown, online age check"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Age Calculator</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Exact Age Calculator & Birthday Tracker
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Calculate your exact chronological age in years, months, and days, alongside total days lived and your birth weekday.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={18} style={{ color: 'var(--primary)' }} /> Select Dates
            </h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Reset
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Date of Birth</label>
              <input 
                type="date" 
                value={birthDate} 
                onChange={(e) => setBirthDate(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Target Date (Defaults to Today)</label>
              <input 
                type="date" 
                value={targetDate} 
                onChange={(e) => setTargetDate(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Privacy Notice</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>All date calculations run locally in your browser memory. Your personal birth dates are never transmitted across networks or logged.</div>
            </div>
          </div>
        </div>

        {/* Right Side: Age Dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={18} style={{ color: '#10b981' }} /> Live Age Summary
            </h3>

            {age ? age.error ? (
              <div style={{ padding: '20px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '12px', textAlign: 'center', fontWeight: 600 }}>
                {age.error}
              </div>
            ) : (
              <div>
                {/* Big Display Banner */}
                <div style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 117, 251, 0.1) 100%)', padding: '28px', borderRadius: '16px', border: '1px solid rgba(16, 185, 129, 0.2)', textAlign: 'center', marginBottom: '24px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px' }}>Chronological Age</div>
                  <div style={{ fontSize: '2.4rem', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span>{age.years} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Years</span></span>
                    <span>{age.months} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Months</span></span>
                    <span>{age.days} <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Days</span></span>
                  </div>
                </div>

                {/* Grid Breakdown Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Days Lived</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--primary)' }}>{age.totalDays.toLocaleString()}</div>
                  </div>

                  <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Hours Lived</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#10b981' }}>{age.totalHours.toLocaleString()}</div>
                  </div>

                  <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Minutes</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#f59e0b' }}>{age.totalMinutes.toLocaleString()}</div>
                  </div>
                </div>

                {/* Day of Week Born + Birthday Countdown */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '14px', border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ background: 'rgba(5, 117, 251, 0.1)', color: 'var(--primary)', padding: '10px', borderRadius: '10px' }}>
                      <Clock size={20} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '2px' }}>Day Born</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>{age.bornDayOfWeek}</div>
                    </div>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '18px', borderRadius: '14px', border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', padding: '10px', borderRadius: '10px' }}>
                      <Gift size={20} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '2px' }}>Next Birthday</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#f59e0b' }}>{age.daysToBday} Days Left</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Select your birth date above to calculate age.</div>
            )}

            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <AdToast message={toast} />
              <button 
                onClick={withAd('age-calc-btn', () => alert("Calculation verified client-side."))}
                style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '12px 28px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition-smooth)' }}
              >
                Log Age Snapshot
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="age-calculator" />
    </div>
  );
};

export default AgeCalculator;
